import { aopFactory, AopOptions } from './aop';
import DollyPlugin, { IDollyOptions } from '../../kastjs/dolly';
import merge from 'lodash/merge';
import { instance } from '../store';
import { UPMInstance, genLocalRouterMap } from '../../kastjs/upm';

import type { PluginFunction, BaseTreeItem } from '@kastjs/runtime-core';

type KContext = Parameters<PluginFunction<any>>[0];

// 是否是超链接
const isLink = (path: string) =>
  typeof path === 'string' &&
  /(http|https):\/\/[\w\-_]+(\.[\w\-_]+)+([\w\-.,@?^=%&:/~+#]*[\w\-@?^=%&/~+#])?/.test(path.trim());

const checkAppList = (appList: IDollyOptions['appList'], context: KContext) => {
  const { appSettings } = context;

  if (Array.isArray(appList)) {
    const prefixPathSet = new Set<string>();
    (function getPath(menus: BaseTreeItem[]) {
      menus.forEach(item => {
        if (
          typeof item.path === 'string' &&
          !isLink(item.path) &&
          !!item.component &&
          item.path.split('/')?.[0]
        ) {
          prefixPathSet.add(item.path.split('/')[0]);
        }
        if (Array.isArray(item.children) && item.children.length > 0) {
          getPath(item.children);
        }
      });
    })(appSettings.routes || []);

    appList.forEach(item => {
      if (!item.basename) return false;
      if (item.basename) {
        const basenames = Array.isArray(item.basename)
          ? [item.basename]
          : typeof item.basename === 'string'
          ? [item.basename]
          : [];
        basenames.forEach(bs => {
          if (prefixPathSet.has(bs)) {
            console.error(`[micorApp appList 参数异常]: ${bs} 与本地配置的路由前缀冲突，请更换`);
          }
        });
      }
    });
  }
};

const removeAuthComponent: AopOptions<typeof DollyPlugin>['after'] = (context, _, options) => {
  const { upmInstance = undefined } = instance.getAll<{ upmInstance?: UPMInstance }>();
  // 主应用和没有权限控制的项目不用
  if (options.isMain || options.isPortalBiz || !upmInstance?.routers?.length) return;

  const parentBasename: string =
    options.source === 'crm'
      ? '/crm-v2'
      : // @ts-ignore
        options?.microApp.parentProps?.globalProps?.basename || '';
  const basename = context?.appSettings?.router?.options?.basename || '';
  const menuPath = options?.isPortalBiz ? basename : basename.replace(parentBasename, '');
  const menuMap = genLocalRouterMap(upmInstance?.routers || []) as Map<
    string,
    UPMInstance['routers'][0]
  >;

  (function each(routes) {
    routes.forEach(item => {
      if (
        item.path &&
        item.component &&
        menuMap.get(menuPath + (item.path.startsWith('/') ? '' : '/') + item.path)?.hasAuth ===
          false
      ) {
        item.component = () => null;
      }

      if (item?.children?.length) {
        each(item.children);
      }
    });
  })(context?.appSettings?.routes || []);
};

export const microAppPluginAOP = aopFactory(DollyPlugin, {
  before(context, params, options) {
    const { env, isMain, isPortalBiz, source } = options;
    Array.isArray(params.appList) && checkAppList(params.appList, context);

    /** 是否启用 runtime */
    const enabledRuntime =
      typeof params === 'object' &&
      typeof params?.parentProps?.createParamsConfig === 'function' &&
      typeof params?.parentProps?.name === 'string' &&
      params.parentProps.createParamsConfig(params.parentProps.name)?.enabledRuntime === 1
        ? true
        : params.enabledRuntime;

    return merge<IDollyOptions, IDollyOptions>(
      {
        env:
          env === 'beta' || env === 'production'
            ? 'production'
            : env === 'staging' || env === 'prt'
            ? 'test'
            : 'development',
        type: isMain ? 'main' : isPortalBiz ? 'mix' : 'child',
        globalProps: {
          basename: context.appSettings.router?.options?.basename,
        },
        source,
        // 门户业务线下，默认由门户控制子应用启用 enabledRuntime
        enabledRuntime,
        // @ts-ignore
        ...(options?.axios && typeof options?.axios === 'object'
          ? // @ts-ignore
            { axiosInstance: options?.axios?.instance }
          : {}),
      },
      params
    );
  },
  after(context, params, options) {
    removeAuthComponent(context, params, options);
    // 隐藏子应用的 404 页面
    if (!options.isMain && context?.appSettings?.router?.notFoundRender) {
      context.appSettings.router.notFoundRender = undefined;
    }
  },
});
